import { useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import {
  BaseFile,
  FileUploader,
  FileUploaderItem,
  FileUploaderV2RefType,
} from '@mint-lib/tw-components/patterns';

import { S3UploadCredentials } from '../../api/types.js';
import { useImportHubV2 } from '../../hooks/ff/index.js';
import {
  failUploadOperation,
  startUploadOperation,
} from '../../operations/uploadOperation.js';
import {
  useAwsFileUploadData,
  useUploadFileMutation,
} from '../../queries/index.js';

type UploadFileDialogProps = {
  onClose: () => void;
  dataFileId: string;
};

const formats = ['.csv', '.xlsx'];
const maxSizeMB = 50;
const maxSizeBytes = megabytesToBytes(maxSizeMB);

export const UploadFileDialog = ({
  onClose,
  dataFileId,
}: UploadFileDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const fileUploaderRef = useRef<FileUploaderV2RefType>(null);
  const hasFailedUploadRef = useRef(false);
  const isImportHubV2 = useImportHubV2();

  const [files, setFiles] = useState<BaseFile[]>([]);
  const { data: awsUploadData } = useAwsFileUploadData(dataFileId, {
    throwOnError: true,
  });
  const { mutateAsync: uploadFile, isPending: isUploadPending } =
    useUploadFileMutation(dataFileId);

  const handleCloseDialog = () => {
    if (isUploadPending) {
      return;
    }
    onClose();
  };

  const handleUploadFile = async (fileBlob: File) => {
    const formData = new FormData();

    const { url, fields } = awsUploadData as S3UploadCredentials;

    for (const field in fields) {
      formData.append(field, fields[field as keyof typeof fields]);
    }

    formData.append('file', fileBlob);

    await uploadFile({
      awsUrl: url,
      payload: formData,
    });
    handleCloseDialog();
  };

  const uploadableFiles = files.filter(
    (file) =>
      !!file.fileBlob &&
      !file.validationError &&
      !file.uploadError &&
      !file.isUploading,
  );

  const updateFileState = (cid: string, patch: Partial<BaseFile>) => {
    fileUploaderRef?.current?.setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.cid === cid ? { ...file, ...patch } : file,
      ),
    );
  };

  const startFileUpload = (fileToRetry: BaseFile) => {
    if (!fileToRetry.fileBlob) return;

    // Ended by the data file socket's parse result (`useAsyncUpload`), or
    // below when the upload or the parse request fails. The dialog cannot be
    // closed while the upload is pending; leaving the wizard abandons it.
    startUploadOperation(
      dataFileId,
      {
        importHubV2: isImportHubV2,
        fileFormat: fileToRetry.fileBlob.name.toLowerCase().endsWith('.csv')
          ? 'csv'
          : 'xlsx',
      },
      { retry: hasFailedUploadRef.current },
    );

    updateFileState(fileToRetry.cid, {
      isUploading: true,
      uploadError: null,
      isUploaded: false,
    });

    handleUploadFile(fileToRetry.fileBlob)
      .catch((error: unknown) => {
        hasFailedUploadRef.current = true;
        failUploadOperation(dataFileId);
        updateFileState(fileToRetry.cid, {
          uploadError: {
            message:
              error instanceof Error ? error.message : t('Upload failed'),
            code: 'upload-error',
          },
        });
      })
      .finally(() => {
        updateFileState(fileToRetry.cid, { isUploading: false });
      });
  };

  const submitUploadFile = () => {
    const [fileToUpload] = uploadableFiles;
    if (fileToUpload) {
      startFileUpload(fileToUpload);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCloseDialog();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange} modal>
      <DialogPortal>
        <DialogOverlay data-dd-action-name="Upload file dialog: close" />
        <DialogRoot size="s">
          <DialogHeader data-dd-action-name="Upload file dialog: close">
            <DialogTitle>{t('Upload file')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('Choose a CSV or Excel file to upload for this import.')}
            </DialogDescription>
          </DialogHeader>
          <DialogContent>
            <div data-dd-action-name="Upload file dialog: select file">
              <FileUploader
                ref={fileUploaderRef}
                onChange={setFiles}
                onUpload={handleUploadFile}
                accept={formats}
                maxSize={maxSizeBytes}
                maxFiles={1}
                fullWidth={true}
                helperText={t('The file should not exceed 50,000 rows')}
                renderItem={({ key, ...props }) => (
                  <div
                    key={key}
                    className="contents"
                    data-dd-action-name="Upload file dialog: file actions"
                  >
                    <FileUploaderItem
                      key={key}
                      {...props}
                      onRetry={() => startFileUpload(props.baseFile)}
                    />
                  </div>
                )}
              />
            </div>
          </DialogContent>
          <DialogFooter>
            <Button
              data-dd-action-name="Upload file dialog: cancel"
              variant="text"
              size="large"
              onClick={handleCloseDialog}
              disabled={isUploadPending}
            >
              {t('Cancel')}
            </Button>
            <Button
              data-dd-action-name="Upload file dialog: next"
              size="large"
              onClick={submitUploadFile}
              disabled={
                !uploadableFiles.length || !awsUploadData || isUploadPending
              }
            >
              {t('Next')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

function megabytesToBytes(megabytes: number): number {
  const bytesInMegabyte = 1024 * 1024;
  return megabytes * bytesInMegabyte;
}
